/**
 * Share map link dialog — full E2E:
 * guest → sign-in (resume) → publish → copy/WhatsApp link.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LoginIcon from '@mui/icons-material/Login';
import ShareIcon from '@mui/icons-material/Share';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { addNotification } from '../store/slices/uiSlice';
import { fetchLayers } from '../store/slices/layersSlice';
import {
  prepareShareAuthRedirect,
  publishShareLink,
  copyShareUrl,
  pickShareLayer,
  peekPendingShareIntent,
  remapPendingShareLayer,
} from '../services/shareMapService';
import { claimGuestWorkspace } from '../services/guestWorkspaceService';
import { trackEvent } from '../services/analyticsService';

interface ShareMapDialogProps {
  open: boolean;
  onClose: () => void;
  /** When true, skip intro and auto-run publish (post-login resume) */
  autoPublish?: boolean;
}

type Phase = 'idle' | 'auth_required' | 'publishing' | 'ready' | 'error';

export default function ShareMapDialog({
  open,
  onClose,
  autoPublish = false,
}: ShareMapDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isRestoring = useAppSelector((s) => s.auth.isRestoring);
  const layers = useAppSelector((s) => s.layers.layers);
  const activeLayerId = useAppSelector((s) => s.layers.activeLayerId);

  const [phase, setPhase] = useState<Phase>('idle');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [featureCount, setFeatureCount] = useState<number | undefined>();
  const [created, setCreated] = useState(false);

  const reset = useCallback(() => {
    setPhase('idle');
    setShareUrl(null);
    setError(null);
    setFeatureCount(undefined);
    setCreated(false);
  }, []);

  const runPublish = useCallback(async () => {
    setPhase('publishing');
    setError(null);
    try {
      const pendingBeforeClaim = peekPendingShareIntent();
      const currentPreferredId = pendingBeforeClaim?.preferredLayerId || activeLayerId || undefined;

      // Claim first so a pending guest layer can be mapped to its exact cloud layer before refresh.
      if (isAuthenticated) {
        const claim = await claimGuestWorkspace();
        if (claim.success) remapPendingShareLayer(claim.layers);
      }

      const preferredLayerId = peekPendingShareIntent()?.preferredLayerId || currentPreferredId;
      let layersForShare = layers;
      if (isAuthenticated) {
        try {
          const fresh = await dispatch(fetchLayers()).unwrap();
          if (Array.isArray(fresh) && fresh.length > 0) {
            const freshLayers = fresh as typeof layers;
            // Keep a local intended layer when it has not been cloud-backed yet.
            layersForShare =
              preferredLayerId && !pickShareLayer(freshLayers, preferredLayerId)
                ? layers
                : freshLayers;
          }
        } catch {
          /* use current redux layers (incl. local demo / guest) */
        }
      }

      if (!layersForShare?.length && layers?.length) {
        layersForShare = layers;
      }

      const result = await publishShareLink(layersForShare || [], {
        preferredLayerId,
        skipClaim: true,
      });
      if (result.needsAuth) {
        setPhase('auth_required');
        return;
      }
      if (!result.success || !result.shareUrl) {
        setPhase('error');
        setError(
          result.message ||
            'Could not create share link. Draw or import a map feature first, then try again.'
        );
        return;
      }
      setShareUrl(result.shareUrl);
      setFeatureCount(result.featureCount);
      setCreated(!!result.created);
      setPhase('ready');
      dispatch(
        addNotification({
          type: 'success',
          message: 'Share link ready — copy and send it to anyone.',
        })
      );
    } catch (e) {
      setPhase('error');
      setError(e instanceof Error ? e.message : 'Share failed');
    }
  }, [activeLayerId, dispatch, isAuthenticated, layers]);

  // On open: decide auth vs publish
  useEffect(() => {
    if (!open || isRestoring) return;

    if (!isAuthenticated) {
      setPhase('auth_required');
      // Snapshot layers for claim after login
      prepareShareAuthRedirect(
        layers,
        `${location.pathname}${location.search || ''}` || '/map',
        activeLayerId || undefined
      );
      trackEvent('share_dialog_auth', {});
      return;
    }

    if (autoPublish || phase === 'idle') {
      void runPublish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when open/auth changes
  }, [open, isAuthenticated, isRestoring, autoPublish]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleSignIn = () => {
    const { loginPath } = prepareShareAuthRedirect(
      layers,
      location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/map')
        ? location.pathname
        : '/map',
      activeLayerId || undefined
    );
    trackEvent('share_signin_click', {});
    onClose();
    navigate(loginPath);
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    const ok = await copyShareUrl(shareUrl);
    dispatch(
      addNotification({
        type: ok ? 'success' : 'error',
        message: ok ? 'Link copied to clipboard' : 'Could not copy — select the link manually',
      })
    );
  };

  const handleWhatsApp = () => {
    if (!shareUrl) return;
    const text = encodeURIComponent(
      `📍 Shared map on GeoVera\n\nView the interactive map:\n${shareUrl}`
    );
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const chosen = pickShareLayer(
    layers,
    peekPendingShareIntent()?.preferredLayerId || activeLayerId || undefined
  );
  const hasFeatures = (chosen?.features?.length || 0) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon color="primary" />
          <Typography component="span" sx={{ fontWeight: 800, fontSize: 18 }}>
            Share map link
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'auth_required' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Sign in free with Google to create a public view-only link. Your current map is saved
              and will open after login so you can get the link.
            </Alert>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>
              Anyone with the link can view the map — they do not need an account. Editing stays on
              your account.
            </Typography>
            {!hasFeatures && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No drawn features yet. You can still sign in — add a boundary or points after login,
                then share again. Demo sample maps can be shared as a new copy after sign-in.
              </Alert>
            )}
            {chosen && (
              <Chip
                size="small"
                label={`Ready: ${chosen.name || 'map layer'}${
                  hasFeatures ? ` · ${chosen.features?.length} feature(s)` : ''
                }`}
                sx={{ mb: 1 }}
              />
            )}
          </Box>
        )}

        {phase === 'publishing' && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}
          >
            <CircularProgress />
            <Typography sx={{ fontWeight: 600 }}>Creating your public share link…</Typography>
            <Typography variant="body2" color="text.secondary">
              Publishing map to the cloud and enabling /m/… link
            </Typography>
          </Box>
        )}

        {phase === 'ready' && shareUrl && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {created
                ? 'Map published. Anyone can open this link (view-only).'
                : 'Share link ready for your cloud layer.'}
              {typeof featureCount === 'number' ? ` · ${featureCount} feature(s)` : ''}
            </Alert>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Public link</Typography>
            <TextField
              fullWidth
              size="small"
              value={shareUrl}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
              sx={{ mb: 2, fontFamily: 'monospace' }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Copy link
              </Button>
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsApp}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                WhatsApp
              </Button>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Open
              </Button>
            </Stack>
          </Box>
        )}

        {phase === 'error' && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || 'Something went wrong'}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tip: draw a boundary or import a layer, then try Share again. Fair-use limits may
              apply if you have many cloud layers.
            </Typography>
            <Button
              variant="contained"
              onClick={() => void runPublish()}
              sx={{ textTransform: 'none' }}
            >
              Try again
            </Button>
          </Box>
        )}

        {phase === 'idle' && isAuthenticated && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
        {phase === 'auth_required' && (
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={handleSignIn}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Sign in free to get link
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
